class VanityBuilder extends HTMLElement {
    constructor() {
        super();

        this.initialized = false;
        this.handleOptionClick = this.handleOptionClick.bind(this);
        this.handleColourGroupToggleClick = this.handleColourGroupToggleClick.bind(this);
        this.handleToneModeToggleClick = this.handleToneModeToggleClick.bind(this);
        this.handleGroupToggleClick = this.handleGroupToggleClick.bind(this);
        this.handleNextClick = this.handleNextClick.bind(this);
        this.handleStepClick = this.handleStepClick.bind(this);
        this.handleAddClick = this.handleAddClick.bind(this);
        this.handleQuantityClick = this.handleQuantityClick.bind(this);
        this.handleQuantityInput = this.handleQuantityInput.bind(this);
        this.handleImagePreviewEnter = this.handleImagePreviewEnter.bind(this);
        this.handleImagePreviewLeave = this.handleImagePreviewLeave.bind(this);
        this.handleImagePreviewMove = this.handleImagePreviewMove.bind(this);
        this.handleOptionInfoEnter = this.handleOptionInfoEnter.bind(this);
        this.handleOptionInfoLeave = this.handleOptionInfoLeave.bind(this);
        this.handleOptionInfoMove = this.handleOptionInfoMove.bind(this);
        this.handleOptionScrollbarResize = this.queueOptionScrollbarUpdate.bind(this);
        this.handleStickySummaryState = this.handleStickySummaryState.bind(this);
        this.handleSummarySheetClick = this.handleSummarySheetClick.bind(this);
        this.handleSummarySheetKeydown = this.handleSummarySheetKeydown.bind(this);
        this.handleStickyQuantityChange = this.handleStickyQuantityChange.bind(this);
    }

    connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.deferHiddenDependentPanels();
        this.groups = Array.from(this.querySelectorAll('[data-vanity-group]'));
        this.steps = Array.from(this.querySelectorAll('[data-vanity-step]'));
        this.progressNav = this.querySelector('.vanity-builder__progress');
        this.summaryList = this.querySelector('[data-vanity-summary-list]');
        this.totalEl = this.querySelector('[data-vanity-total]');
        this.compareTotalEl = this.querySelector('[data-vanity-compare-total]');
        this.addButton = this.querySelector('[data-vanity-builder-add]');
        this.requiredMessage = this.querySelector('[data-vanity-required-message]');
        this.addOnNotice = this.querySelector('[data-vanity-addon-notice]');
        this.quantity = 1;
        this.basePriceCents = this.toNumber(this.dataset.basePriceCents, 0);
        this.baseComparePriceCents = Math.max(
            this.basePriceCents,
            this.toNumber(this.dataset.baseComparePriceCents, this.basePriceCents),
        );
        this.discountPercent = this.getValidDiscountPercent(this.dataset.discountPercent);
        this.optionCache = new Map();
        this.dependentPanelCache = new Map();
        this.panelOptionCache = new Map();
        this.colourPanelCache = new Map();
        this.groupLookup = new Map();
        this.tileScrollers = Array.from(this.querySelectorAll('.vanity-builder-options--tiles'));
        this.optionScrollbarStates = [];
        this.optionScrollbarStateByScroller = new Map();
        this.lastScrolledProgressKey = '';

        this.groups.forEach(group => {
            this.optionCache.set(group, Array.from(group.querySelectorAll('[data-vanity-option]')));
            this.dependentPanelCache.set(group, Array.from(group.querySelectorAll('[data-dependent-options]')));
            this.colourPanelCache.set(group, Array.from(group.querySelectorAll('[data-colour-panel]')));
            this.registerGroupLookup(group.dataset.groupKey, group);
            this.getGroupOptions(group).forEach(option => {
                this.registerGroupLookup(option.dataset.optionScopeKey, group);
            });
            this.syncGroupCollapseState(group);

            group.querySelector('.vanity-builder-group__header')?.addEventListener('click', this.handleGroupToggleClick);
            group.querySelector('[data-vanity-group-next]')?.addEventListener('click', this.handleNextClick);
            group.querySelectorAll('[data-colour-group-toggle]').forEach(toggle => {
                toggle.addEventListener('click', this.handleColourGroupToggleClick);
            });
            group.querySelectorAll('[data-tone-mode-toggle]').forEach(toggle => {
                toggle.addEventListener('click', this.handleToneModeToggleClick);
            });
        });

        this.steps.forEach(step => {
            step.querySelector('a')?.addEventListener('click', this.handleStepClick);
        });

        this.addButton?.addEventListener('click', this.handleAddClick);
        this.addEventListener('click', this.handleOptionClick);
        this.addEventListener('click', this.handleQuantityClick);
        this.addEventListener('input', this.handleQuantityInput);
        this.addEventListener('change', this.handleQuantityInput);
        this.addEventListener('focusout', this.handleQuantityInput);
        document.addEventListener('sticky-atc:quantity-change', this.handleStickyQuantityChange);
        this.initOptionScrollbars();
        this.bindImagePreview();
        this.bindOptionInfoTooltips();
        this.renderDiscountedOptionPrices();
        this.syncQuantityControls();
        this.update();
        this.initMobileSummarySheet();
    }

    disconnectedCallback() {
        this.groups?.forEach(group => {
            group.querySelector('.vanity-builder-group__header')?.removeEventListener('click', this.handleGroupToggleClick);
            group.querySelector('[data-vanity-group-next]')?.removeEventListener('click', this.handleNextClick);
            group.querySelectorAll('[data-colour-group-toggle]').forEach(toggle => {
                toggle.removeEventListener('click', this.handleColourGroupToggleClick);
            });
            group.querySelectorAll('[data-tone-mode-toggle]').forEach(toggle => {
                toggle.removeEventListener('click', this.handleToneModeToggleClick);
            });
        });

        this.steps?.forEach(step => {
            step.querySelector('a')?.removeEventListener('click', this.handleStepClick);
        });

        this.addButton?.removeEventListener('click', this.handleAddClick);
        this.removeEventListener('click', this.handleOptionClick);
        this.removeEventListener('click', this.handleQuantityClick);
        this.removeEventListener('input', this.handleQuantityInput);
        this.removeEventListener('change', this.handleQuantityInput);
        this.removeEventListener('focusout', this.handleQuantityInput);
        document.removeEventListener('sticky-atc:quantity-change', this.handleStickyQuantityChange);
        this.unbindImagePreview();
        this.unbindOptionInfoTooltips();
        this.destroyOptionScrollbars();
        this.destroyMobileSummarySheet();
        this.imagePreviewEl?.remove();
        this.imagePreviewEl = null;
        this.optionInfoTooltipEl?.remove();
        this.optionInfoTooltipEl = null;
    }

    handleOptionClick(event) {
        const option = event.target?.closest?.('[data-vanity-option]');
        if (!option || !this.contains(option)) return;

        const group = option.closest('[data-vanity-group]');
        if (!group || option.classList.contains('is-hidden') || option.classList.contains('is-colour-group-hidden')) return;

        const isMultiSelect = group.dataset.multiSelect === 'true';
        const isRequired = group.dataset.required === 'true';
        const isSelected = option.classList.contains('is-selected');
        const optionScope = option.dataset.optionScope;

        if (isMultiSelect) {
            this.setOptionSelected(option, !isSelected);
        } else if ((!isRequired || option.dataset.optionOptional === 'true') && isSelected) {
            this.setOptionSelected(option, false);
        } else if (optionScope) {
            this.getGroupOptions(group).filter(groupOption => groupOption.dataset.optionScope === optionScope).forEach(groupOption => {
                this.setOptionSelected(groupOption, groupOption === option);
            });

            if (option.dataset.dependentScope) {
                this.getGroupOptions(group).filter(groupOption => groupOption.dataset.optionScope === option.dataset.dependentScope).forEach(groupOption => {
                    this.setOptionSelected(groupOption, false);
                });
            }
        } else {
            this.getGroupOptions(group).forEach(groupOption => {
                this.setOptionSelected(groupOption, groupOption === option);
            });
        }

        this.update();
        this.scrollToNextBenchtopSuboption(group, option);
    }

    handleColourGroupToggleClick(event) {
        const toggle = event.currentTarget;
        const colourPanel = toggle.closest('[data-colour-panel]');
        const group = toggle.closest('[data-vanity-group]');
        const colourGroup = toggle.dataset.colourGroupValue;
        if (!group || !colourGroup) return;

        if (colourPanel) {
            colourPanel.dataset.activeColourGroup = colourGroup;
        } else {
            group.dataset.activeColourGroup = colourGroup;
        }

        this.update();
    }

    handleToneModeToggleClick(event) {
        const toggle = event.currentTarget;
        const group = toggle.closest('[data-vanity-group]');
        const toneMode = toggle.dataset.toneModeValue;
        if (!group || !toneMode) return;

        group.dataset.toneMode = toneMode;
        this.update();
    }

    handleGroupToggleClick(event) {
        const group = event.currentTarget.closest('[data-vanity-group]');
        if (!group) return;

        this.setGroupCollapsed(group, !group.classList.contains('is-collapsed'));
        this.queueOptionScrollbarUpdate();
    }

    handleNextClick(event) {
        const group = event.currentTarget.closest('[data-vanity-group]');
        const nextGroup = this.getNextGroup(group);
        if (!group || !nextGroup) return;

        this.setGroupCollapsed(group, true);
        this.setGroupCollapsed(nextGroup, false);
        this.queueOptionScrollbarUpdate();
        nextGroup.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    handleStepClick(event) {
        const link = event.currentTarget;
        const target = link.hash ? document.querySelector(link.hash) : null;
        if (!target) return;

        event.preventDefault();
        this.setGroupCollapsed(target, false);
        this.queueOptionScrollbarUpdate();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    handleAddClick() {
        if (this.addButton?.disabled) {
            this.scrollToFirstIncompleteGroup();
            return;
        }

        this.addToCart();
    }

    async addToCart() {
        if (this.isAdding) return;

        const cart = this.buildCartPayload();
        if (!cart) return;

        const cartDrawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartDrawer) {
            cart.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        }

        this.setAddLoading(true);

        try {
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(cart),
            });
            const responseJson = await response.json();

            if (!response.ok) throw responseJson;

            if (cartDrawer) {
                cartDrawer.renderContents(responseJson, { openCompleteLook: true });
                if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
            }
        } catch (error) {
            console.error('Vanity builder cart add failed => ', error);
        } finally {
            this.setAddLoading(false);
        }
    }

    buildCartPayload() {
        const variantId = this.dataset.variantId;
        if (!variantId) return null;

        const selections = this.getSelections();
        const quantity = this.getQuantity();
        const totalCents = this.getTotalCents(selections);
        const compareTotalCents = this.getCompareTotalCents(selections);
        const addOnOptions = this.getSelectedAddOnOptions(selections);
        const addOnPriceCents = addOnOptions.reduce((sum, option) => sum + option.priceCents, 0);
        const vanityPriceCents = Math.max(0, totalCents - addOnPriceCents);
        const vanityComparePriceCents = Math.max(vanityPriceCents, compareTotalCents - addOnPriceCents);
        const vanityUid = this.createVanityUid();
        const parentSku = this.dataset.productSku || '';
        const parentReference = parentSku || this.dataset.productTitle || 'custom vanity';

        const mainLine = {
            id: variantId,
            quantity,
            properties: {
                ...this.getVisibleCartProperties(selections),
                _custom_vanity: 'true',
                _custom_vanity_uid: vanityUid,
                _custom_vanity_price_cents: String(vanityPriceCents),
                _custom_vanity_compare_price_cents: String(vanityComparePriceCents),
                _custom_vanity_total_cents: String(totalCents),
                _custom_vanity_total_compare_cents: String(compareTotalCents),
                _custom_vanity_add_on_price_cents: String(addOnPriceCents),
                _custom_vanity_discount_percent: String(this.discountPercent),
            },
        };

        const addOnLines = addOnOptions
            .filter(option => option.addOnVariantId)
            .map(option => ({
                id: option.addOnVariantId,
                quantity: Math.max(1, Math.ceil(option.addOnQuantity || 1)) * quantity,
                properties: {
                    'Added with': parentReference,
                    _custom_vanity_add_on: 'true',
                    _custom_vanity_uid: vanityUid,
                    _custom_vanity_parent_sku: parentSku,
                    _custom_vanity_parent_variant_id: variantId,
                },
            }));

        return {
            items: [mainLine, ...addOnLines],
        };
    }

    getVisibleCartProperties(selections) {
        const properties = {};

        selections.forEach(selection => {
            const visibleOptions = selection.options.filter(option => !option.isAddOnProduct);
            if (!visibleOptions.length) return;

            const propertyName = this.getUniquePropertyName(properties, selection.groupLabel);
            properties[propertyName] = visibleOptions.map(option => option.label).join(', ');
        });

        return properties;
    }

    getSelectedAddOnOptions(selections) {
        return selections.flatMap(selection => selection.options.filter(option => option.isAddOnProduct));
    }

    getUniquePropertyName(properties, label) {
        const baseLabel = String(label || 'Selection').trim() || 'Selection';
        let propertyName = baseLabel;
        let index = 2;

        while (Object.prototype.hasOwnProperty.call(properties, propertyName)) {
            propertyName = `${baseLabel} ${index}`;
            index += 1;
        }

        return propertyName;
    }

    createVanityUid() {
        return `vanity-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }

    setAddLoading(isLoading) {
        this.isAdding = isLoading;

        if (this.addButton) {
            this.addButton.disabled = isLoading || !this.areRequiredGroupsComplete();
            this.addButton.classList.toggle('custom-loading', isLoading);
            this.addButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        }

        document.querySelectorAll('[data-vanity-builder-scroll][data-vanity-builder-ready]').forEach(button => {
            button.classList.toggle('custom-loading', isLoading);
            button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
            button.querySelector('.loader')?.classList.toggle('d-none', !isLoading);
        });
    }

    dispatchAddEvent() {
        this.dispatchEvent(new CustomEvent('vanity-builder:add', {
            bubbles: true,
            detail: {
                selections: this.getSelections(),
                totalCents: this.getTotalCents(),
                quantity: this.getQuantity(),
            },
        }));
    }

    handleQuantityClick(event) {
        const decreaseButton = event.target.closest('[data-vanity-summary-quantity-minus]');
        const increaseButton = event.target.closest('[data-vanity-summary-quantity-plus]');
        const button = decreaseButton || increaseButton;
        if (!button) return;

        event.preventDefault();
        this.setQuantity(this.getQuantity() + (increaseButton ? 1 : -1));
    }

    handleQuantityInput(event) {
        if (!event.target.matches('[data-vanity-summary-quantity-input]')) return;
        if (event.type === 'input') return;

        this.setQuantity(event.target.value);
    }

    handleStickyQuantityChange(event) {
        if (!event.detail || event.detail.quantity == null) return;

        this.setQuantity(event.detail.quantity, { syncSticky: false });
    }

    getQuantity() {
        return this.normalizeQuantity(this.quantity);
    }

    setQuantity(value, options = {}) {
        this.quantity = this.normalizeQuantity(value);
        this.syncQuantityControls();
        if (options.syncSticky !== false) this.syncStickyQuantityControl();
        this.updateSummary();

        if (this.summarySheet?.classList.contains('is-open')) {
            this.renderSummarySheet();
        }
    }

    normalizeQuantity(value) {
        const quantity = Math.ceil(Number(value));
        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    syncQuantityControls() {
        const roots = [this, this.summarySheet].filter(Boolean);

        roots.forEach(root => {
            root.querySelectorAll('[data-vanity-summary-quantity-input]').forEach(input => {
                input.value = String(this.getQuantity());
            });

            root.querySelectorAll('[data-vanity-summary-quantity-minus]').forEach(button => {
                if (this.getQuantity() <= 1) {
                    button.setAttribute('disabled', 'true');
                } else {
                    button.removeAttribute('disabled');
                }
            });
        });
    }

    syncStickyQuantityControl() {
        const stickyBar = document.querySelector('[data-sticky-atc]');
        if (!stickyBar) return;

        const quantity = String(this.getQuantity());
        const quantityInput = stickyBar.querySelector('[data-sticky-atc-quantity]');
        const quantityHidden = stickyBar.querySelector('[data-sticky-atc-quantity-hidden]');
        const quantityMinus = stickyBar.querySelector('[data-sticky-atc-quantity-minus]');

        if (quantityInput) quantityInput.value = quantity;
        if (quantityHidden) quantityHidden.value = quantity;

        if (quantityMinus) {
            if (this.getQuantity() <= 1) {
                quantityMinus.setAttribute('disabled', 'true');
            } else {
                quantityMinus.removeAttribute('disabled');
            }
        }
    }

    scrollToNextBenchtopSuboption(group, option) {
        if (!this.isBenchtopGroup(group) || !option.classList.contains('is-selected')) return;

        const currentPanel = option.closest('[data-dependent-options]');
        const visiblePanels = this.getDependentPanels(group).filter(panel => {
            return this.isDependentPanelVisible(panel) && this.hasVisibleOptions(panel);
        });

        if (!visiblePanels.length) return;

        const currentIndex = currentPanel ? visiblePanels.indexOf(currentPanel) : -1;
        const nextPanel = visiblePanels.slice(currentIndex + 1).find(panel => panel !== currentPanel);
        if (!nextPanel) return;

        const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

        requestAnimationFrame(() => {
            nextPanel.scrollIntoView({
                behavior: prefersReducedMotion ? 'auto' : 'smooth',
                block: 'start',
            });
        });
    }

    initOptionScrollbars() {
        window.addEventListener('resize', this.handleOptionScrollbarResize);
        this.queueOptionScrollbarUpdate();
    }

    ensureVisibleOptionScrollbars() {
        this.tileScrollers?.forEach(scroller => {
            if (this.optionScrollbarStateByScroller?.has(scroller)) return;
            if (scroller.offsetParent === null) return;

            const state = this.createOptionScrollbarState(scroller);
            this.optionScrollbarStateByScroller.set(scroller, state);
            this.optionScrollbarStates.push(state);
        });
    }

    createOptionScrollbarState(scroller) {
        const controls = document.createElement('div');
        const prevButton = this.createOptionScrollButton('prev');
        const nextButton = this.createOptionScrollButton('next');
        const expandButton = this.createOptionExpandButton();
        const scrollbar = document.createElement('div');
        const thumb = document.createElement('span');

        controls.className = 'vanity-builder-options-controls';
        controls.setAttribute('data-vanity-options-controls', '');
        controls.hidden = true;
        controls.append(prevButton, nextButton, expandButton);
        scrollbar.className = 'vanity-builder-options-scrollbar';
        scrollbar.setAttribute('data-vanity-options-scrollbar', '');
        scrollbar.setAttribute('aria-hidden', 'true');
        thumb.className = 'vanity-builder-options-scrollbar__thumb';

        scrollbar.append(thumb);
        scroller.insertAdjacentElement('beforebegin', controls);
        scroller.insertAdjacentElement('afterend', scrollbar);

        const state = {
            scroller,
            controls,
            prevButton,
            nextButton,
            expandButton,
            scrollbar,
            thumb,
            startX: 0,
            startScrollLeft: 0,
            handleScroll: () => this.updateOptionScrollbar(state),
            handlePrevClick: () => this.scrollOptionScroller(state, -1),
            handleNextClick: () => this.scrollOptionScroller(state, 1),
            handleExpandClick: () => this.toggleOptionScrollerExpanded(state),
            handleTrackPointerDown: event => this.handleOptionScrollbarTrackPointerDown(event, state),
            handleThumbPointerDown: event => this.handleOptionScrollbarThumbPointerDown(event, state),
            handlePointerMove: event => this.handleOptionScrollbarPointerMove(event, state),
            handlePointerUp: event => this.handleOptionScrollbarPointerUp(event, state),
        };

        scroller.addEventListener('scroll', state.handleScroll, { passive: true });
        prevButton.addEventListener('click', state.handlePrevClick);
        nextButton.addEventListener('click', state.handleNextClick);
        expandButton.addEventListener('click', state.handleExpandClick);
        scrollbar.addEventListener('pointerdown', state.handleTrackPointerDown);
        thumb.addEventListener('pointerdown', state.handleThumbPointerDown);

        return state;
    }

    destroyOptionScrollbars() {
        window.removeEventListener('resize', this.handleOptionScrollbarResize);
        cancelAnimationFrame(this.optionScrollbarFrame);

        this.optionScrollbarStates?.forEach(state => {
            state.scroller?.removeEventListener('scroll', state.handleScroll);
            state.prevButton?.removeEventListener('click', state.handlePrevClick);
            state.nextButton?.removeEventListener('click', state.handleNextClick);
            state.expandButton?.removeEventListener('click', state.handleExpandClick);
            state.scrollbar?.removeEventListener('pointerdown', state.handleTrackPointerDown);
            state.thumb?.removeEventListener('pointerdown', state.handleThumbPointerDown);
            document.removeEventListener('pointermove', state.handlePointerMove);
            document.removeEventListener('pointerup', state.handlePointerUp);
            document.removeEventListener('pointercancel', state.handlePointerUp);
            state.controls?.remove();
            state.scrollbar?.remove();
        });

        this.optionScrollbarStates = [];
        this.optionScrollbarStateByScroller = new Map();
        this.optionScrollbarFrame = null;
    }

    queueOptionScrollbarUpdate() {
        if (this.optionScrollbarFrame) return;

        this.optionScrollbarFrame = requestAnimationFrame(() => {
            this.optionScrollbarFrame = null;
            this.updateOptionScrollbars();
        });
    }

    updateOptionScrollbars() {
        this.ensureVisibleOptionScrollbars();
        this.optionScrollbarStates?.forEach(state => this.updateOptionScrollbar(state));
    }

    updateOptionScrollbar(state) {
        if (!state?.scroller || !state.scrollbar || !state.thumb) return;

        const maxScrollLeft = state.scroller.scrollWidth - state.scroller.clientWidth;
        const isScrollable = state.scroller.offsetParent !== null && maxScrollLeft > 4;
        const isExpanded = state.scroller.classList.contains('is-expanded');

        if (state.controls) {
            state.controls.hidden = !isScrollable && !isExpanded;
        }

        state.scrollbar.hidden = !isScrollable || isExpanded;
        state.scrollbar.classList.toggle('is-scrollable', isScrollable && !isExpanded);

        if (state.prevButton) state.prevButton.hidden = isExpanded;
        if (state.nextButton) state.nextButton.hidden = isExpanded;
        if (state.expandButton) {
            state.expandButton.hidden = !isScrollable && !isExpanded;
            state.expandButton.textContent = isExpanded ? 'Slider' : 'View all';
            state.expandButton.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        }

        if (!isScrollable || isExpanded) return;

        const trackWidth = state.scrollbar.clientWidth || 0;
        if (!trackWidth) return;

        const minThumbWidth = Math.min(115, trackWidth);
        const proportionalThumbWidth = trackWidth * (state.scroller.clientWidth / state.scroller.scrollWidth);
        const thumbWidth = Math.max(minThumbWidth, Math.min(proportionalThumbWidth, trackWidth));
        const maxThumbOffset = trackWidth - thumbWidth;
        const scrollRatio = maxScrollLeft > 0 ? state.scroller.scrollLeft / maxScrollLeft : 0;

        state.thumb.style.width = `${thumbWidth}px`;
        state.thumb.style.transform = `translateX(${maxThumbOffset * scrollRatio}px)`;

        if (state.prevButton) state.prevButton.disabled = state.scroller.scrollLeft <= 1;
        if (state.nextButton) state.nextButton.disabled = state.scroller.scrollLeft >= maxScrollLeft - 1;
    }

    createOptionScrollButton(direction) {
        const button = document.createElement('button');
        const isNext = direction === 'next';

        button.type = 'button';
        button.className = `vanity-builder-options-control vanity-builder-options-control--${direction}`;
        button.setAttribute('aria-label', isNext ? 'Scroll options right' : 'Scroll options left');
        button.innerHTML = `
            <svg class="icon" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="${isNext ? 'M5 3L9 7L5 11' : 'M9 3L5 7L9 11'}" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
        `;

        return button;
    }

    scrollOptionScroller(state, direction) {
        if (!state?.scroller) return;

        const scrollAmount = Math.max(state.scroller.clientWidth * 0.85, 160);

        state.scroller.scrollBy({
            left: scrollAmount * direction,
            behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
    }

    createOptionExpandButton() {
        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'vanity-builder-options-expand';
        button.textContent = 'View all';
        button.setAttribute('aria-expanded', 'false');

        return button;
    }

    toggleOptionScrollerExpanded(state) {
        if (!state?.scroller) return;

        const isExpanded = state.scroller.classList.toggle('is-expanded');
        state.scroller.scrollLeft = 0;
        state.expandButton?.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        this.queueOptionScrollbarUpdate();
    }

    handleOptionScrollbarTrackPointerDown(event, state) {
        if (event.target === state.thumb) return;

        event.preventDefault();

        const rect = state.scrollbar.getBoundingClientRect();
        const thumbWidth = state.thumb.offsetWidth || 0;
        const maxThumbOffset = Math.max(rect.width - thumbWidth, 1);
        const ratio = Math.max(0, Math.min((event.clientX - rect.left - thumbWidth / 2) / maxThumbOffset, 1));
        const maxScrollLeft = state.scroller.scrollWidth - state.scroller.clientWidth;

        state.scroller.scrollTo({
            left: maxScrollLeft * ratio,
            behavior: 'smooth',
        });
    }

    handleOptionScrollbarThumbPointerDown(event, state) {
        event.preventDefault();
        event.stopPropagation();

        state.startX = event.clientX;
        state.startScrollLeft = state.scroller.scrollLeft;
        state.thumb.classList.add('is-dragging');

        document.addEventListener('pointermove', state.handlePointerMove);
        document.addEventListener('pointerup', state.handlePointerUp);
        document.addEventListener('pointercancel', state.handlePointerUp);
    }

    handleOptionScrollbarPointerMove(event, state) {
        const trackWidth = state.scrollbar.clientWidth || 0;
        const thumbWidth = state.thumb.offsetWidth || 0;
        const maxThumbOffset = trackWidth - thumbWidth;
        const maxScrollLeft = state.scroller.scrollWidth - state.scroller.clientWidth;

        if (maxThumbOffset <= 0 || maxScrollLeft <= 0) return;

        const deltaX = event.clientX - state.startX;
        state.scroller.scrollLeft = state.startScrollLeft + deltaX * (maxScrollLeft / maxThumbOffset);
    }

    handleOptionScrollbarPointerUp(event, state) {
        state.thumb.classList.remove('is-dragging');

        document.removeEventListener('pointermove', state.handlePointerMove);
        document.removeEventListener('pointerup', state.handlePointerUp);
        document.removeEventListener('pointercancel', state.handlePointerUp);
    }

    bindImagePreview() {
        if (!this.canUseImagePreview()) return;

        this.addEventListener('mouseover', this.handleImagePreviewEnter);
        this.addEventListener('mousemove', this.handleImagePreviewMove);
        this.addEventListener('mouseout', this.handleImagePreviewLeave);
    }

    unbindImagePreview() {
        this.removeEventListener('mouseover', this.handleImagePreviewEnter);
        this.removeEventListener('mousemove', this.handleImagePreviewMove);
        this.removeEventListener('mouseout', this.handleImagePreviewLeave);
    }

    handleImagePreviewEnter(event) {
        const media = this.getPreviewMediaFromEvent(event);
        if (!media || media.contains(event.relatedTarget)) return;

        const image = media.querySelector('img');
        if (!image || !this.canUseImagePreview()) return;

        const preview = this.getImagePreview();
        const previewImage = preview.querySelector('[data-vanity-image-preview-image]');
        const previewLabel = preview.querySelector('[data-vanity-image-preview-label]');
        const option = media.closest('[data-vanity-option]');
        const label = option?.dataset.optionLabel || image.alt || '';

        previewImage.src = this.getPreviewImageSrc(image);
        previewImage.alt = image.alt || label;
        previewLabel.textContent = label;
        previewLabel.hidden = !label;
        preview.classList.toggle('is-mirrored', media.classList.contains('vanity-builder-option__media--mirror'));

        this.positionImagePreview(media);
        preview.classList.add('is-visible');
    }

    handleImagePreviewMove(event) {
        const media = this.getPreviewMediaFromEvent(event);
        if (media) this.positionImagePreview(media);
    }

    handleImagePreviewLeave(event) {
        const media = this.getPreviewMediaFromEvent(event);
        if (!media || media.contains(event.relatedTarget)) return;

        this.imagePreviewEl?.classList.remove('is-visible', 'is-mirrored');
    }

    bindOptionInfoTooltips() {
        if (!this.canUseImagePreview()) return;

        this.addEventListener('mouseover', this.handleOptionInfoEnter);
        this.addEventListener('mousemove', this.handleOptionInfoMove);
        this.addEventListener('mouseout', this.handleOptionInfoLeave);
    }

    unbindOptionInfoTooltips() {
        this.removeEventListener('mouseover', this.handleOptionInfoEnter);
        this.removeEventListener('mousemove', this.handleOptionInfoMove);
        this.removeEventListener('mouseout', this.handleOptionInfoLeave);
    }

    handleOptionInfoEnter(event) {
        const trigger = this.getOptionInfoFromEvent(event);
        if (!trigger || trigger.contains(event.relatedTarget)) return;

        const tooltipText = trigger.dataset.tooltip;
        if (!tooltipText || !this.canUseImagePreview()) return;

        const tooltip = this.getOptionInfoTooltip();

        tooltip.textContent = tooltipText;
        this.positionOptionInfoTooltip(trigger);
        tooltip.classList.add('is-visible');
    }

    handleOptionInfoMove(event) {
        const trigger = this.getOptionInfoFromEvent(event);
        if (trigger) this.positionOptionInfoTooltip(trigger);
    }

    handleOptionInfoLeave(event) {
        const trigger = this.getOptionInfoFromEvent(event);
        if (!trigger || trigger.contains(event.relatedTarget)) return;

        this.optionInfoTooltipEl?.classList.remove('is-visible');
    }

    getPreviewMediaFromEvent(event) {
        const media = event.target?.closest?.('.vanity-builder-option__media');
        if (!media || !this.contains(media) || !media.querySelector('img')) return null;

        return media;
    }

    getOptionInfoFromEvent(event) {
        const trigger = event.target?.closest?.('.vanity-builder-option__info[data-tooltip]');
        if (!trigger || !this.contains(trigger)) return null;

        return trigger;
    }

    getOptionInfoTooltip() {
        if (this.optionInfoTooltipEl) return this.optionInfoTooltipEl;

        const tooltip = document.createElement('div');
        tooltip.className = 'vanity-builder-info-tooltip';
        tooltip.setAttribute('aria-hidden', 'true');

        document.body.append(tooltip);
        this.optionInfoTooltipEl = tooltip;

        return tooltip;
    }

    positionOptionInfoTooltip(trigger) {
        if (!this.optionInfoTooltipEl) return;

        const gap = 10;
        const rect = trigger.getBoundingClientRect();
        const tooltipWidth = this.optionInfoTooltipEl.offsetWidth || 260;
        const tooltipHeight = this.optionInfoTooltipEl.offsetHeight || 0;
        let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        let top = rect.top - tooltipHeight - gap;

        if (top < gap) {
            top = rect.bottom + gap;
        }

        left = Math.max(gap, Math.min(left, window.innerWidth - tooltipWidth - gap));
        top = Math.max(gap, Math.min(top, window.innerHeight - tooltipHeight - gap));

        this.optionInfoTooltipEl.style.left = `${left}px`;
        this.optionInfoTooltipEl.style.top = `${top}px`;
    }

    getImagePreview() {
        if (this.imagePreviewEl) return this.imagePreviewEl;

        const preview = document.createElement('div');
        preview.className = 'vanity-builder-image-preview';
        preview.setAttribute('aria-hidden', 'true');
        preview.innerHTML = `
            <span class="vanity-builder-image-preview__label" data-vanity-image-preview-label></span>
            <img data-vanity-image-preview-image alt="">
        `;

        document.body.append(preview);
        this.imagePreviewEl = preview;

        return preview;
    }

    positionImagePreview(media) {
        if (!this.imagePreviewEl) return;

        const gap = 14;
        const rect = media.getBoundingClientRect();
        const previewSize = Math.min(420, Math.max(280, window.innerWidth * 0.32));
        let left = rect.right + gap;
        let top = rect.top + (rect.height / 2) - (previewSize / 2);

        if (left + previewSize > window.innerWidth - gap) {
            left = rect.left - previewSize - gap;
        }

        left = Math.max(gap, Math.min(left, window.innerWidth - previewSize - gap));
        top = Math.max(gap, Math.min(top, window.innerHeight - previewSize - gap));

        this.imagePreviewEl.style.width = `${previewSize}px`;
        this.imagePreviewEl.style.left = `${left}px`;
        this.imagePreviewEl.style.top = `${top}px`;
    }

    getPreviewImageSrc(image) {
        const source = image.currentSrc || image.src;
        if (!source) return '';

        try {
            const url = new URL(source, window.location.origin);
            if (url.searchParams.has('width')) {
                url.searchParams.set('width', '900');
            }

            return url.toString();
        } catch (error) {
            return source.replace(/([?&]width=)\d+/, '$1900');
        }
    }

    canUseImagePreview() {
        return window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;
    }

    update() {
        this.updateToneModes();
        this.updateColourGroups();
        this.updateAvailability();
        this.updateDependentOptions();
        this.updateAutoSelectedGroups();
        this.updateGroupVisibility();
        this.updateGroupState();
        this.updateSummary();
        this.queueOptionScrollbarUpdate();
        this.handleStickySummaryState();

        if (this.summarySheet?.classList.contains('is-open')) {
            this.renderSummarySheet();
        }
    }

    initMobileSummarySheet() {
        this.mobileSummaryMedia = window.matchMedia?.('(max-width: 749px)');
        window.addEventListener('scroll', this.handleStickySummaryState, { passive: true });
        window.addEventListener('resize', this.handleStickySummaryState);

        const stickyButton = this.getStickyVanityButton();
        if (stickyButton && window.MutationObserver) {
            this.stickySummaryObserver = new MutationObserver(this.handleStickySummaryState);
            this.stickySummaryObserver.observe(stickyButton, {
                attributes: true,
                attributeFilter: ['aria-controls', 'data-vanity-builder-scroll', 'disabled'],
                childList: true,
                subtree: true,
            });
        }

        this.handleStickySummaryState();
    }

    destroyMobileSummarySheet() {
        window.removeEventListener('scroll', this.handleStickySummaryState);
        window.removeEventListener('resize', this.handleStickySummaryState);
        document.removeEventListener('keydown', this.handleSummarySheetKeydown);
        this.stickySummaryObserver?.disconnect();
        this.stickySummaryObserver = null;

        if (this.stickySummaryFrame) {
            cancelAnimationFrame(this.stickySummaryFrame);
            this.stickySummaryFrame = null;
        }

        this.resetStickySummaryButton();
        this.summarySheet?.removeEventListener('click', this.handleSummarySheetClick);
        this.summarySheet?.removeEventListener('click', this.handleQuantityClick);
        this.summarySheet?.removeEventListener('input', this.handleQuantityInput);
        this.summarySheet?.removeEventListener('change', this.handleQuantityInput);
        this.summarySheet?.removeEventListener('focusout', this.handleQuantityInput);
        this.summarySheet?.remove();
        this.summarySheet = null;
        document.body.classList.remove('vanity-builder-summary-sheet-open');
    }

    handleStickySummaryState() {
        if (this.stickySummaryFrame) return;

        this.stickySummaryFrame = requestAnimationFrame(() => {
            this.stickySummaryFrame = null;
            this.updateStickySummaryButton();
        });
    }

    updateStickySummaryButton() {
        const button = this.getStickyVanityButton();
        if (!button) return;

        if (!this.isMobileSummaryViewport() && this.summarySheet?.classList.contains('is-open')) {
            this.closeSummarySheet();
        }

        const requiredComplete = this.areRequiredGroupsComplete();
        button.toggleAttribute('data-vanity-builder-ready', requiredComplete);

        if (requiredComplete) {
            button.removeAttribute('data-vanity-summary-trigger');
            button.setAttribute('aria-label', 'Add to cart');
            this.setStickyButtonText(button, 'Add to Cart');
            return;
        }

        const shouldOpenSummary = this.shouldStickyOpenSummary();
        button.removeAttribute('data-vanity-builder-ready');
        button.toggleAttribute('data-vanity-summary-trigger', shouldOpenSummary);
        button.setAttribute('aria-label', shouldOpenSummary ? 'View your customisation' : 'Start customising your vanity');
        this.setStickyButtonText(button, shouldOpenSummary ? 'View your customisation' : 'Start Customising Your Vanity');
    }

    resetStickySummaryButton() {
        const button = this.getStickyVanityButton();
        if (!button) return;

        button.removeAttribute('data-vanity-summary-trigger');
        button.removeAttribute('data-vanity-builder-ready');
        button.removeAttribute('aria-label');
        this.setStickyButtonText(button, 'Start Customising Your Vanity');
    }

    shouldStickyOpenSummary() {
        if (!this.isMobileSummaryViewport()) return false;

        const rect = this.getBoundingClientRect();
        return rect.top <= 80;
    }

    isMobileSummaryViewport() {
        if (this.mobileSummaryMedia) return this.mobileSummaryMedia.matches;
        return window.innerWidth < 750;
    }

    getStickyVanityButton() {
        return document.querySelector('[data-sticky-atc-button][data-vanity-builder-scroll]');
    }

    setStickyButtonText(button, text) {
        const label = button.querySelector('.vanity-builder-cta-label');
        const textEl = label
            ? Array.from(label.children).find(child => !child.classList.contains('vanity-builder-cta-label__icon'))
            : null;

        if (textEl) {
            if (textEl.textContent.trim() !== text) textEl.textContent = text;
            return;
        }

        const fallbackTextEl = button.querySelector('span:not(.loader)');
        if (fallbackTextEl && fallbackTextEl.textContent.trim() !== text) {
            fallbackTextEl.textContent = text;
        }
    }

    openSummarySheet() {
        if (!this.isMobileSummaryViewport()) return;

        const sheet = this.ensureSummarySheet();
        this.renderSummarySheet();

        sheet.classList.add('is-open');
        sheet.setAttribute('aria-hidden', 'false');
        document.body.classList.add('vanity-builder-summary-sheet-open');
        document.addEventListener('keydown', this.handleSummarySheetKeydown);
        sheet.querySelector('[data-vanity-summary-sheet-close]')?.focus();
    }

    closeSummarySheet() {
        if (!this.summarySheet) return;

        this.summarySheet.classList.remove('is-open');
        this.summarySheet.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('vanity-builder-summary-sheet-open');
        document.removeEventListener('keydown', this.handleSummarySheetKeydown);
    }

    ensureSummarySheet() {
        if (this.summarySheet) return this.summarySheet;

        const sheet = document.createElement('div');
        sheet.className = 'vanity-builder-summary-sheet';
        sheet.setAttribute('role', 'dialog');
        sheet.setAttribute('aria-modal', 'true');
        sheet.setAttribute('aria-hidden', 'true');
        sheet.setAttribute('aria-label', 'Your customisation');
        sheet.innerHTML = `
            <button type="button" class="vanity-builder-summary-sheet__backdrop" data-vanity-summary-sheet-close aria-label="Close customisation summary"></button>
            <div class="vanity-builder-summary-sheet__panel" role="document">
                <div class="vanity-builder-summary-sheet__header">
                    <h3 class="vanity-builder-summary-sheet__title">Your customisation</h3>
                    <button type="button" class="vanity-builder-summary-sheet__close" data-vanity-summary-sheet-close aria-label="Close customisation summary">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="vanity-builder-summary-sheet__content product-custom-container" data-vanity-summary-sheet-content></div>
            </div>
        `;

        sheet.addEventListener('click', this.handleSummarySheetClick);
        sheet.addEventListener('click', this.handleQuantityClick);
        sheet.addEventListener('input', this.handleQuantityInput);
        sheet.addEventListener('change', this.handleQuantityInput);
        sheet.addEventListener('focusout', this.handleQuantityInput);
        document.body.append(sheet);
        this.summarySheet = sheet;

        return sheet;
    }

    renderSummarySheet() {
        if (!this.summarySheet) return;

        const source = this.querySelector('.vanity-builder-summary');
        const content = this.summarySheet.querySelector('[data-vanity-summary-sheet-content]');
        if (!source || !content) return;

        content.innerHTML = '';
        const clone = source.cloneNode(true);
        clone.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
        content.append(clone);
        this.syncQuantityControls();
    }

    deferHiddenDependentPanels() {
        this.querySelectorAll('[data-dependent-options][hidden]').forEach(panel => {
            if (panel.dataset.deferred === 'true') return;

            const template = document.createElement('template');
            template.setAttribute('data-vanity-deferred-panel', '');

            while (panel.firstChild) {
                template.content.append(panel.firstChild);
            }

            panel.append(template);
            panel.dataset.deferred = 'true';
        });
    }

    hydrateDependentPanel(panel) {
        if (!panel || panel.dataset.deferred !== 'true') return;

        const template = panel.querySelector('template[data-vanity-deferred-panel]');
        if (!template) return;

        const group = panel.closest('[data-vanity-group]');
        const content = template.content;

        template.remove();
        panel.append(content);
        delete panel.dataset.deferred;

        this.panelOptionCache?.delete(panel);
        this.refreshGroupCaches(group);
        this.getPanelOptions(panel).forEach(option => this.syncOptionAvailability(option));
        this.renderDiscountedOptionPrices(panel);
    }

    refreshGroupCaches(group) {
        if (!group) return;

        this.optionCache?.set(group, Array.from(group.querySelectorAll('[data-vanity-option]')));
        this.dependentPanelCache?.set(group, Array.from(group.querySelectorAll('[data-dependent-options]')));
        this.colourPanelCache?.set(group, Array.from(group.querySelectorAll('[data-colour-panel]')));

        this.getGroupOptions(group).forEach(option => {
            this.registerGroupLookup(option.dataset.optionScopeKey, group);
        });

        const scrollerSet = new Set(this.tileScrollers || []);
        group.querySelectorAll('.vanity-builder-options--tiles').forEach(scroller => scrollerSet.add(scroller));
        this.tileScrollers = Array.from(scrollerSet);
    }

    getGroupOptions(group) {
        if (!group) return [];

        if (!this.optionCache?.has(group)) {
            this.optionCache?.set(group, Array.from(group.querySelectorAll('[data-vanity-option]')));
        }

        return this.optionCache?.get(group) || [];
    }

    getPanelOptions(panel) {
        if (!panel) return [];

        if (!this.panelOptionCache?.has(panel)) {
            this.panelOptionCache?.set(panel, Array.from(panel.querySelectorAll('[data-vanity-option]')));
        }

        return this.panelOptionCache?.get(panel) || [];
    }

    getOptionsForTarget(target) {
        if (!target) return [];

        if (target.matches?.('[data-vanity-group]')) return this.getGroupOptions(target);
        if (target.matches?.('[data-dependent-options], [data-colour-panel]')) return this.getPanelOptions(target);

        const group = target.closest?.('[data-vanity-group]');
        return group ? this.getGroupOptions(group) : [];
    }

    getDependentPanels(group) {
        if (!group) return [];

        if (!this.dependentPanelCache?.has(group)) {
            this.dependentPanelCache?.set(group, Array.from(group.querySelectorAll('[data-dependent-options]')));
        }

        return this.dependentPanelCache?.get(group) || [];
    }

    getColourPanels(group) {
        if (!group) return [];

        if (!this.colourPanelCache?.has(group)) {
            this.colourPanelCache?.set(group, Array.from(group.querySelectorAll('[data-colour-panel]')));
        }

        return this.colourPanelCache?.get(group) || [];
    }

    registerGroupLookup(groupKey, group) {
        const normalizedGroupKey = this.normalizeKey(groupKey);
        if (!normalizedGroupKey || this.groupLookup?.has(normalizedGroupKey)) return;

        this.groupLookup?.set(normalizedGroupKey, group);
    }

    getGroupByKey(groupKey) {
        const normalizedGroupKey = this.normalizeKey(groupKey);
        if (!normalizedGroupKey) return null;

        return this.groupLookup?.get(normalizedGroupKey) || null;
    }

    handleSummarySheetClick(event) {
        if (event.target.closest('[data-vanity-summary-sheet-close]')) {
            event.preventDefault();
            this.closeSummarySheet();
            return;
        }

        if (event.target.closest('[data-vanity-builder-add]')) {
            event.preventDefault();
            this.addButton?.click();
        }
    }

    handleSummarySheetKeydown(event) {
        if (event.key === 'Escape') this.closeSummarySheet();
    }

    updateAutoSelectedGroups() {
        this.groups.forEach(group => {
            if (group.dataset.autoSelectFirst !== 'true' || !this.isGroupAvailable(group)) return;
            if (this.getSelectedOptions(group).length) return;

            const firstOption = this.getGroupOptions(group).find(option => this.isOptionVisible(option));
            if (firstOption) this.setOptionSelected(firstOption, true);
        });
    }

    updateAvailability() {
        this.groups.forEach(group => {
            this.getGroupOptions(group).forEach(option => {
                this.syncOptionAvailability(option);
            });
        });
    }

    syncOptionAvailability(option) {
        const isAvailable = this.isOptionAvailable(option);
        option.classList.toggle('is-hidden', !isAvailable);
        option.hidden = !isAvailable;

        if (!isAvailable && option.classList.contains('is-selected')) {
            this.setOptionSelected(option, false);
        }
    }

    updateColourGroups() {
        this.groups.forEach(group => {
            const colourPanels = this.getColourPanels(group);
            const targets = colourPanels.length ? colourPanels : [group];

            targets.forEach(target => {
                const toggles = Array.from(target.querySelectorAll('[data-colour-group-toggle]'));
                if (!toggles.length) return;

                const allowedColourGroups = this.getAllowedColourGroupsForTarget(target, group, toggles);
                const firstAllowedToggle = toggles.find(toggle => allowedColourGroups.has(toggle.dataset.colourGroupValue));
                let activeColourGroup = target.dataset.activeColourGroup || group.dataset.activeColourGroup || firstAllowedToggle?.dataset.colourGroupValue || toggles[0].dataset.colourGroupValue;

                if (!allowedColourGroups.has(activeColourGroup)) {
                    activeColourGroup = firstAllowedToggle?.dataset.colourGroupValue || '';
                }

                target.dataset.activeColourGroup = activeColourGroup;
                this.updateColourGroupToggleVisibility(target, group, allowedColourGroups, toggles);

                toggles.forEach(toggle => {
                    const isAllowed = allowedColourGroups.has(toggle.dataset.colourGroupValue);

                    toggle.hidden = !isAllowed;
                    toggle.classList.toggle('is-hidden', !isAllowed);
                    toggle.setAttribute('aria-pressed', isAllowed && toggle.dataset.colourGroupValue === activeColourGroup ? 'true' : 'false');
                });

                this.getOptionsForTarget(target, group).filter(option => option.dataset.colourGroup).forEach(option => {
                    const isAllowedColourGroup = allowedColourGroups.has(option.dataset.colourGroup);
                    const isVisibleColourGroup = isAllowedColourGroup && option.dataset.colourGroup === activeColourGroup;

                    option.classList.toggle('is-colour-group-hidden', !isVisibleColourGroup);

                    if (!isVisibleColourGroup && option.classList.contains('is-selected')) {
                        this.setOptionSelected(option, false);
                    }
                });
            });
        });
    }

    getAllowedColourGroupsForTarget(target, group, toggles) {
        const allowedColourGroups = new Set(toggles.map(toggle => toggle.dataset.colourGroupValue).filter(Boolean));

        if (target.dataset.forceStandardPaint === 'true') {
            allowedColourGroups.delete('standard-woodgrain');
            return allowedColourGroups;
        }

        if (!this.isCabinetColourGroup(group) || group.dataset.routingColourFilter !== 'true') {
            return allowedColourGroups;
        }

        const colourScope = target.dataset.colourScope || '';
        if (colourScope === 'side') {
            return allowedColourGroups;
        }

        const routingGroupKey = group.dataset.routingSourceKey || 'routing_panel_design';
        const selectedRoutingOption = this.getSelectedOptionsForGroupKey(routingGroupKey)[0];
        const routingAllowsWoodgrain = selectedRoutingOption?.dataset.allowsWoodgrain === 'true';

        if (!routingAllowsWoodgrain) {
            allowedColourGroups.delete('standard-woodgrain');
        }

        return allowedColourGroups;
    }

    updateColourGroupToggleVisibility(target, group, allowedColourGroups, toggles) {
        const toggleWrapper = toggles[0]?.closest('.vanity-builder-colour-toggle');
        if (!toggleWrapper) return;

        const availableColourGroups = this.getAvailableColourGroupsForTarget(target, allowedColourGroups);
        const isForcedStandardPaint = target.dataset.forceStandardPaint === 'true';
        const isRoutingRestrictedPrimaryColour = this.isCabinetColourGroup(group) &&
            group.dataset.routingColourFilter === 'true' &&
            target.dataset.colourScope !== 'side' &&
            !allowedColourGroups.has('standard-woodgrain');

        const shouldHideToggle = isForcedStandardPaint || isRoutingRestrictedPrimaryColour || availableColourGroups.size < 2;

        toggleWrapper.hidden = shouldHideToggle;
        toggleWrapper.classList.toggle('is-hidden', shouldHideToggle);
    }

    getAvailableColourGroupsForTarget(target, allowedColourGroups) {
        const availableColourGroups = new Set();

        this.getOptionsForTarget(target).filter(option => option.dataset.colourGroup).forEach(option => {
            const colourGroup = option.dataset.colourGroup;
            if (!colourGroup || !allowedColourGroups.has(colourGroup)) return;
            if (!this.isOptionAvailable(option)) return;

            availableColourGroups.add(colourGroup);
        });

        return availableColourGroups;
    }

    updateToneModes() {
        this.groups.forEach(group => {
            const toggles = Array.from(group.querySelectorAll('[data-tone-mode-toggle]'));
            if (!toggles.length && !this.isCabinetColourGroup(group)) return;

            if (group.dataset.forceDualTone === 'true') {
                group.dataset.toneMode = 'dual';
            }

            const toneMode = group.dataset.toneMode || 'single';
            group.dataset.toneMode = toneMode;
            group.classList.toggle('is-single-tone', toneMode === 'single');
            group.classList.toggle('is-dual-tone', toneMode === 'dual');

            toggles.forEach(toggle => {
                toggle.setAttribute('aria-pressed', toggle.dataset.toneModeValue === toneMode ? 'true' : 'false');
            });

            this.getColourPanels(group).forEach(panel => {
                const isSidePanel = panel.dataset.colourScope === 'side';
                const isHidden = isSidePanel && toneMode !== 'dual';

                panel.classList.toggle('is-tone-panel-hidden', isHidden);
                panel.hidden = isHidden;

                if (isHidden) {
                    this.getPanelOptions(panel).filter(option => option.classList.contains('is-selected')).forEach(option => {
                        this.setOptionSelected(option, false);
                    });
                }
            });
        });
    }

    updateDependentOptions() {
        this.groups.forEach(group => {
            this.getDependentPanels(group).forEach(panel => {
                const parentKey = panel.dataset.dependentParentKey;
                const parentScope = panel.dataset.dependentParentScope;
                const parentUid = panel.dataset.dependentParentUid;
                const parentOption = this.getGroupOptions(group).find(option => {
                    if (parentUid) {
                        if (option.dataset.optionUid !== parentUid) return false;
                    } else if (option.dataset.optionKey !== parentKey) {
                        return false;
                    }

                    if (!parentScope) return true;

                    return option.dataset.optionScope === parentScope;
                });
                const isVisible = Boolean(parentOption && this.isOptionVisible(parentOption) && parentOption.classList.contains('is-selected'));

                if (isVisible) {
                    this.hydrateDependentPanel(panel);
                }

                panel.classList.toggle('is-hidden', !isVisible);
                panel.hidden = !isVisible;

                if (!isVisible) {
                    this.getPanelOptions(panel).filter(option => option.classList.contains('is-selected')).forEach(option => {
                        this.setOptionSelected(option, false);
                    });
                } else if (panel.dataset.autoSelectFirst === 'true' && !this.getSelectedOptionsForPanel(panel).length) {
                    const firstOption = this.getPanelOptions(panel).find(option => this.isOptionVisible(option));
                    if (firstOption) this.setOptionSelected(firstOption, true);
                }
            });
        });
    }

    updateGroupVisibility() {
        this.groups.forEach(group => {
            const isAvailable = this.isGroupAvailable(group);
            const steps = this.getStepsForGroup(group.dataset.groupKey);

            group.classList.toggle('is-hidden', !isAvailable);
            group.hidden = !isAvailable;

            steps.forEach(step => {
                step.classList.toggle('is-hidden', !isAvailable);
                step.hidden = !isAvailable;
            });

            if (!isAvailable) {
                this.getGroupOptions(group).filter(option => option.classList.contains('is-selected')).forEach(option => {
                    this.setOptionSelected(option, false);
                });
                this.setGroupCollapsed(group, true);
            }
        });
    }

    updateGroupState() {
        const nextPendingGroup = this.groups.find(group => this.isGroupAvailable(group) && this.isGroupPending(group));

        this.groups.forEach(group => {
            const groupKey = group.dataset.groupKey;
            const selectedOptions = this.getSelectedOptions(group);
            const status = group.querySelector('[data-vanity-group-status]');
            const steps = this.getStepsForGroup(groupKey);
            const nextButton = group.querySelector('[data-vanity-group-next]');
            const emptyMessage = group.querySelector('[data-vanity-empty-message]');
            const isAvailable = this.isGroupAvailable(group);
            const isComplete = isAvailable && this.isGroupComplete(group) && selectedOptions.length > 0;
            const isActive = isAvailable && group === nextPendingGroup;

            if (status) {
                const statusPrefix = group.dataset.groupSubtitle ? `${group.dataset.groupSubtitle} - ` : '';
                const statusText = this.getGroupStatusText(group, selectedOptions);

                status.textContent = `${statusPrefix}${statusText}`;
            }

            steps.forEach(step => {
                step.classList.toggle('is-complete', isComplete);
                step.classList.toggle('is-active', isActive);
                step.querySelector('a')?.setAttribute('aria-current', isActive ? 'step' : 'false');
            });

            if (nextButton) {
                nextButton.hidden = (group.dataset.required === 'true' && !this.isGroupComplete(group)) || !this.getNextGroup(group);
            }

            if (emptyMessage) {
                emptyMessage.hidden = this.hasVisibleOptions(group);
            }
        });

        this.scrollProgressToGroup(nextPendingGroup || this.getLastAvailableGroup());
    }

    getStepsForGroup(groupKey) {
        const normalizedGroupKey = this.normalizeKey(groupKey);

        return this.steps.filter(step => this.normalizeKey(step.dataset.stepKey) === normalizedGroupKey);
    }

    getProgressStepForGroup(groupKey) {
        const normalizedGroupKey = this.normalizeKey(groupKey);

        return this.steps.find(step => {
            return step.classList.contains('vanity-builder-progress-step')
                && this.normalizeKey(step.dataset.stepKey) === normalizedGroupKey;
        }) || null;
    }

    getLastAvailableGroup() {
        return this.groups.findLast?.(group => this.isGroupAvailable(group))
            || [...this.groups].reverse().find(group => this.isGroupAvailable(group))
            || null;
    }

    scrollProgressToGroup(group) {
        if (!this.progressNav || !group) return;

        const groupKey = this.normalizeKey(group.dataset.groupKey);
        if (!groupKey || this.lastScrolledProgressKey === groupKey) return;

        const step = this.getProgressStepForGroup(groupKey);
        if (!step || step.hidden || step.classList.contains('is-hidden')) return;

        this.lastScrolledProgressKey = groupKey;

        requestAnimationFrame(() => {
            const maxScrollLeft = this.progressNav.scrollWidth - this.progressNav.clientWidth;
            if (maxScrollLeft <= 0) return;

            const navRect = this.progressNav.getBoundingClientRect();
            const stepRect = step.getBoundingClientRect();
            const stepLeft = stepRect.left - navRect.left + this.progressNav.scrollLeft;
            const targetLeft = stepLeft - ((this.progressNav.clientWidth - stepRect.width) / 2);
            const left = Math.max(0, Math.min(targetLeft, maxScrollLeft));

            this.progressNav.scrollTo({
                left,
                behavior: 'smooth',
            });
        });
    }

    updateSummary() {
        const selections = this.getSelections();
        const requiredComplete = this.areRequiredGroupsComplete();
        const totalCents = this.getTotalCents(selections);
        const compareTotalCents = this.getCompareTotalCents(selections);
        const displayTotalCents = this.getDisplayTotalCents(totalCents);
        const displayCompareTotalCents = this.getDisplayTotalCents(compareTotalCents);
        const hasCompareTotal = displayCompareTotalCents > displayTotalCents;
        const hasAddOnProduct = selections.some(selection => {
            return selection.options.some(option => option.isAddOnProduct);
        });

        if (this.summaryList) {
            this.summaryList.innerHTML = '';

            if (selections.length) {
                selections.forEach(selection => {
                    const row = document.createElement('div');
                    row.className = 'vanity-builder-summary__row';

                    const label = document.createElement('span');
                    label.textContent = selection.groupLabel;

                    const value = document.createElement('strong');
                    value.textContent = selection.options.map(option => option.label).join(', ');

                    row.append(label, value);
                    this.summaryList.append(row);
                });
            } else {
                const empty = document.createElement('p');
                empty.className = 'vanity-builder-summary__empty';
                empty.textContent = 'Select your vanity options to see a summary.';
                this.summaryList.append(empty);
            }
        }

        if (this.totalEl) {
            this.totalEl.textContent = this.formatMoney(displayTotalCents);
            this.totalEl.classList.toggle('is-discounted', hasCompareTotal);
        }
        if (this.compareTotalEl) {
            this.compareTotalEl.textContent = hasCompareTotal ? this.formatMoney(displayCompareTotalCents) : '';
            this.compareTotalEl.hidden = !hasCompareTotal;
        }
        if (this.addOnNotice) this.addOnNotice.hidden = !hasAddOnProduct;
        if (this.addButton) this.addButton.disabled = !requiredComplete;
        if (this.requiredMessage) this.requiredMessage.hidden = requiredComplete;
    }

    getSelections() {
        return this.groups
            .flatMap(group => {
                if (!this.isGroupAvailable(group)) return [];

                if (this.isCabinetColourGroup(group)) {
                    const primaryOptions = this.getSelectedOptionsForScope(group, 'primary');
                    const sideOptions = this.getSelectedOptionsForScope(group, 'side');
                    const selections = [];

                    if (primaryOptions.length) {
                        selections.push({
                            groupKey: group.dataset.groupKey,
                            groupLabel: this.isDualToneMode(group) ? 'Door / Drawer Colour' : group.dataset.groupLabel,
                            options: primaryOptions.map(option => this.getSelectionOption(option)),
                        });
                    }

                    if (this.isDualToneMode(group) && sideOptions.length) {
                        const sideScopeKey = sideOptions[0].dataset.optionScopeKey || 'external_carcass_colour';
                        const sideScopeLabel = sideOptions[0].dataset.optionScopeLabel || 'External Carcass Colour';

                        selections.push({
                            groupKey: sideScopeKey,
                            groupLabel: sideScopeLabel,
                            options: sideOptions.map(option => this.getSelectionOption(option)),
                        });
                    }

                    return selections;
                }

                if (this.isHandleGroup(group)) {
                    const handleOptions = this.getSelectedOptionsForScope(group, 'handle');
                    const handleColourOptions = this.getSelectedOptionsForScope(group, 'handle_colour');
                    const selections = [];

                    if (handleOptions.length) {
                        selections.push({
                            groupKey: group.dataset.groupKey,
                            groupLabel: group.dataset.groupLabel,
                            options: handleOptions.map(option => this.getSelectionOption(option)),
                        });
                    }

                    if (handleColourOptions.length) {
                        const colourScopeKey = handleColourOptions[0].dataset.optionScopeKey || 'handle_colour';
                        const colourScopeLabel = handleColourOptions[0].dataset.optionScopeLabel || 'Handle Colour';

                        selections.push({
                            groupKey: colourScopeKey,
                            groupLabel: colourScopeLabel,
                            options: handleColourOptions.map(option => this.getSelectionOption(option)),
                        });
                    }

                    return selections;
                }

                if (this.isLegGroup(group)) {
                    const legOptions = this.getSelectedOptionsForScope(group, 'leg');
                    const legColourOptions = this.getSelectedOptionsForScope(group, 'leg_colour');
                    const selections = [];

                    if (legOptions.length) {
                        selections.push({
                            groupKey: group.dataset.groupKey,
                            groupLabel: group.dataset.groupLabel,
                            options: legOptions.map(option => this.getSelectionOption(option)),
                        });
                    }

                    if (legColourOptions.length) {
                        const colourScopeKey = legColourOptions[0].dataset.optionScopeKey || 'leg_colour';
                        const colourScopeLabel = legColourOptions[0].dataset.optionScopeLabel || 'Leg Colour';

                        selections.push({
                            groupKey: colourScopeKey,
                            groupLabel: colourScopeLabel,
                            options: legColourOptions.map(option => this.getSelectionOption(option)),
                        });
                    }

                    return selections;
                }

                if (this.isBenchtopGroup(group)) {
                    const selectionsByScope = new Map();

                    this.getSelectedOptions(group).forEach(option => {
                        const scopeKey = option.dataset.optionScopeKey || option.dataset.optionScope || group.dataset.groupKey;
                        const scopeLabel = option.dataset.optionScopeLabel || group.dataset.groupLabel;

                        if (!selectionsByScope.has(scopeKey)) {
                            selectionsByScope.set(scopeKey, {
                                groupKey: scopeKey,
                                groupLabel: scopeLabel,
                                options: [],
                            });
                        }

                        selectionsByScope.get(scopeKey).options.push(this.getSelectionOption(option));
                    });

                    return Array.from(selectionsByScope.values());
                }

                if (this.isFillerPanelGroup(group)) {
                    return [
                        {
                            scope: 'left',
                            fallbackKey: 'left_filler_panel',
                            fallbackLabel: 'Left Filler Panel',
                        },
                        {
                            scope: 'right',
                            fallbackKey: 'right_filler_panel',
                            fallbackLabel: 'Right Filler Panel',
                        },
                    ].flatMap(side => {
                        const sideOptions = this.getSelectedOptionsForScope(group, side.scope);
                        if (!sideOptions.length) return [];

                        const sideScopeKey = sideOptions[0].dataset.optionScopeKey || side.fallbackKey;
                        const sideScopeLabel = sideOptions[0].dataset.optionScopeLabel || side.fallbackLabel;

                        return [{
                            groupKey: sideScopeKey,
                            groupLabel: sideScopeLabel,
                            options: sideOptions.map(option => this.getSelectionOption(option)),
                        }];
                    });
                }

                const selectedOptions = this.getSelectedOptions(group);

                return {
                    groupKey: group.dataset.groupKey,
                    groupLabel: group.dataset.groupLabel,
                    options: selectedOptions.map(option => this.getSelectionOption(option)),
                };
            })
            .filter(selection => selection.options.length > 0);
    }

    getSelectionOption(option) {
        return {
            key: option.dataset.optionKey,
            label: option.dataset.optionLabel,
            priceCents: this.getOptionPriceCents(option),
            originalPriceCents: this.getOriginalOptionPriceCents(option),
            quantityMultiplier: option.dataset.quantityMultiplier === 'true',
            isAddOnProduct: option.dataset.addOnProduct === 'true',
            addOnVariantId: option.dataset.addOnVariantId || '',
            addOnQuantity: this.toNumber(option.dataset.addOnQuantity, 1),
        };
    }

    getTotalCents(selections = this.getSelections(), useOriginalPrices = false) {
        const basinQuantity = this.getBasinQuantity();

        return selections.reduce((total, selection) => {
            return total + selection.options.reduce((selectionTotal, option) => {
                const multiplier = option.quantityMultiplier ? basinQuantity : 1;
                const priceCents = useOriginalPrices ? option.originalPriceCents : option.priceCents;

                return selectionTotal + (priceCents * multiplier);
            }, 0);
        }, useOriginalPrices ? this.baseComparePriceCents : this.basePriceCents);
    }

    getCompareTotalCents(selections = this.getSelections()) {
        return this.getTotalCents(selections, true);
    }

    getDisplayTotalCents(totalCents = this.getTotalCents()) {
        return totalCents * this.getQuantity();
    }

    getBasinQuantity() {
        let quantity = 1;

        this.groups.flatMap(group => this.getGroupOptions(group)).filter(option => option.classList.contains('is-selected')).forEach(option => {
            const optionQuantity = this.toNumber(option.dataset.basinQuantity, 1);
            if (optionQuantity > quantity) quantity = optionQuantity;
        });

        return quantity;
    }

    getSelectedOptions(group) {
        return this.getGroupOptions(group).filter(option => option.classList.contains('is-selected') && this.isOptionVisible(option));
    }

    getSelectedOptionsForPanel(panel) {
        return this.getPanelOptions(panel).filter(option => option.classList.contains('is-selected') && this.isOptionVisible(option));
    }

    getSelectedOptionsForScope(group, scope) {
        return this.getSelectedOptions(group).filter(option => option.dataset.optionScope === scope);
    }

    findOption(groupKey, optionKey) {
        const normalizedGroupKey = this.normalizeKey(groupKey);
        const group = this.getGroupByKey(normalizedGroupKey);

        if (!group) return null;

        return this.getGroupOptions(group).find(option => {
            if (option.dataset.optionKey !== optionKey) return false;

            return this.optionBelongsToGroupKey(option, group, normalizedGroupKey);
        }) || null;
    }

    isGroupComplete(group) {
        if (this.isBenchtopGroup(group)) {
            return this.isBenchtopGroupComplete(group);
        }

        if (this.isLegGroup(group)) {
            const selectedLeg = this.getSelectedOptionsForScope(group, 'leg')[0];
            if (!selectedLeg) return group.dataset.required !== 'true';
            if (selectedLeg.dataset.hasDependentOptions !== 'true') return true;

            return this.getSelectedOptionsForScope(group, 'leg_colour').some(option => {
                return option.dataset.parentOptionKey === selectedLeg.dataset.optionKey;
            });
        }

        if (group.dataset.required !== 'true') return true;

        if (this.isCabinetColourGroup(group)) {
            const hasPrimaryColour = this.getSelectedOptionsForScope(group, 'primary').length > 0;
            const hasSidePanelColour = this.getSelectedOptionsForScope(group, 'side').length > 0;

            return this.isDualToneMode(group) ? hasPrimaryColour && hasSidePanelColour : hasPrimaryColour;
        }

        if (this.isHandleGroup(group)) {
            const selectedHandle = this.getSelectedOptionsForScope(group, 'handle')[0];
            if (!selectedHandle) return false;
            if (selectedHandle.dataset.hasDependentOptions !== 'true') return true;

            return this.getSelectedOptionsForScope(group, 'handle_colour').some(option => {
                return option.dataset.parentOptionKey === selectedHandle.dataset.optionKey;
            });
        }

        return this.getSelectedOptions(group).length > 0;
    }

    isGroupPending(group) {
        if (!this.isGroupAvailable(group)) return false;
        if (!this.isGroupComplete(group)) return true;

        if (group.dataset.required === 'true') {
            return false;
        }

        return this.hasVisibleOptions(group) && this.getSelectedOptions(group).length === 0;
    }

    hasVisibleOptions(group) {
        if (!this.isGroupAvailable(group)) return false;

        return this.getGroupOptions(group).some(option => this.isOptionVisible(option));
    }

    areRequiredGroupsComplete() {
        return this.groups.every(group => !this.isGroupAvailable(group) || this.isGroupComplete(group));
    }

    getGroupStatusText(group, selectedOptions) {
        if (this.isCabinetColourGroup(group)) {
            const primaryOption = this.getSelectedOptionsForScope(group, 'primary')[0];
            const sideOption = this.getSelectedOptionsForScope(group, 'side')[0];

            if (!primaryOption) return group.dataset.required === 'true' ? 'Selection required' : 'Optional';
            if (!this.isDualToneMode(group)) return primaryOption.dataset.optionLabel;

            const parts = [`Door / Drawer: ${primaryOption.dataset.optionLabel}`];
            parts.push(sideOption ? `External Carcass: ${sideOption.dataset.optionLabel}` : 'External Carcass: Selection required');

            return parts.join(', ');
        }

        if (this.isHandleGroup(group)) {
            const selectedHandle = this.getSelectedOptionsForScope(group, 'handle')[0];
            if (!selectedHandle) return group.dataset.required === 'true' ? 'Selection required' : 'Optional';
            if (selectedHandle.dataset.hasDependentOptions !== 'true') return selectedHandle.dataset.optionLabel;

            const selectedColour = this.getSelectedOptionsForScope(group, 'handle_colour').find(option => {
                return option.dataset.parentOptionKey === selectedHandle.dataset.optionKey;
            });

            return selectedColour
                ? `${selectedHandle.dataset.optionLabel}, ${selectedColour.dataset.optionLabel}`
                : `${selectedHandle.dataset.optionLabel} - Colour required`;
        }

        if (this.isLegGroup(group)) {
            const selectedLeg = this.getSelectedOptionsForScope(group, 'leg')[0];
            if (!selectedLeg) return group.dataset.required === 'true' ? 'Selection required' : 'Optional';
            if (selectedLeg.dataset.hasDependentOptions !== 'true') return selectedLeg.dataset.optionLabel;

            const selectedColour = this.getSelectedOptionsForScope(group, 'leg_colour').find(option => {
                return option.dataset.parentOptionKey === selectedLeg.dataset.optionKey;
            });

            return selectedColour
                ? `${selectedLeg.dataset.optionLabel}, ${selectedColour.dataset.optionLabel}`
                : `${selectedLeg.dataset.optionLabel} - Colour required`;
        }

        if (this.isFillerPanelGroup(group)) {
            const leftOption = this.getSelectedOptionsForScope(group, 'left')[0];
            const rightOption = this.getSelectedOptionsForScope(group, 'right')[0];
            const parts = [];

            if (leftOption) parts.push(`Left: ${leftOption.dataset.optionLabel}`);
            if (rightOption) parts.push(`Right: ${rightOption.dataset.optionLabel}`);

            return parts.length ? parts.join(', ') : 'Optional';
        }

        if (this.isBenchtopGroup(group)) {
            const selectedBenchtop = this.getSelectedOptionsForScope(group, 'benchtop')[0];
            if (!selectedBenchtop) return 'Selection required';

            const incompletePanel = this.getIncompleteRequiredDependentPanel(group);
            if (incompletePanel) {
                const title = incompletePanel.querySelector('.vanity-builder-dependent-options__title')?.textContent?.trim();
                return `${selectedBenchtop.dataset.optionLabel} - ${title || 'Selection'} required`;
            }

            const selectedSubOptions = selectedOptions.filter(option => option.dataset.optionScope !== 'benchtop');
            if (!selectedSubOptions.length) return selectedBenchtop.dataset.optionLabel;

            return [
                selectedBenchtop.dataset.optionLabel,
                ...selectedSubOptions.map(option => option.dataset.optionLabel),
            ].join(', ');
        }

        if (selectedOptions.length) {
            return selectedOptions.map(option => option.dataset.optionLabel).join(', ');
        }

        return group.dataset.required === 'true' ? 'Selection required' : 'Optional';
    }

    isCabinetColourGroup(group) {
        return group?.dataset.cabinetColourGroup === 'true';
    }

    isHandleGroup(group) {
        return group?.dataset.handleGroup === 'true';
    }

    isLegGroup(group) {
        return group?.dataset.legGroup === 'true';
    }

    isFillerPanelGroup(group) {
        return group?.dataset.fillerPanelGroup === 'true';
    }

    isBenchtopGroup(group) {
        return group?.dataset.benchtopGroup === 'true';
    }

    isBenchtopGroupComplete(group) {
        const selectedBenchtop = this.getSelectedOptionsForScope(group, 'benchtop')[0];
        if (!selectedBenchtop) return false;
        if (selectedBenchtop.dataset.benchtopType === 'none') return true;

        return !this.getIncompleteRequiredDependentPanel(group);
    }

    getIncompleteRequiredDependentPanel(group) {
        return this.getDependentPanels(group).filter(panel => panel.dataset.dependentRequired === 'true').find(panel => {
            if (panel.hidden || panel.classList.contains('is-hidden')) return false;

            const visibleOptions = this.getPanelOptions(panel).filter(option => this.isOptionVisible(option));
            if (!visibleOptions.length) return false;

            return !visibleOptions.some(option => option.classList.contains('is-selected'));
        }) || null;
    }

    isDualToneMode(group) {
        return this.isCabinetColourGroup(group) && group.dataset.toneMode === 'dual';
    }

    setOptionSelected(option, selected) {
        option.classList.toggle('is-selected', selected);
        option.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }

    syncGroupCollapseState(group) {
        this.setGroupCollapsed(group, group.classList.contains('is-collapsed'));
    }

    setGroupCollapsed(group, collapsed) {
        group.classList.toggle('is-collapsed', collapsed);

        const toggle = group.querySelector('[data-vanity-group-toggle]');
        const body = group.querySelector('[data-vanity-group-body]');

        toggle?.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        if (body) body.hidden = collapsed;
    }

    getNextGroup(group) {
        const index = this.groups.indexOf(group);
        if (index < 0) return null;

        return this.groups.slice(index + 1).find(nextGroup => this.isGroupAvailable(nextGroup)) || null;
    }

    isGroupAvailable(group) {
        const visibleIfGroup = group?.dataset.visibleIfGroup;
        const visibleIfColourGroup = group?.dataset.visibleIfColourGroup;

        if (!visibleIfGroup && !visibleIfColourGroup) return true;

        const selectedOptions = visibleIfGroup ? this.getSelectedOptionsForGroupKey(visibleIfGroup) : [];

        if (visibleIfColourGroup) {
            const normalizedColourGroup = this.normalizeKey(visibleIfColourGroup);

            return selectedOptions.some(option => this.normalizeKey(option.dataset.colourGroup) === normalizedColourGroup);
        }

        return selectedOptions.length > 0;
    }

    getSelectedOptionsForGroupKey(groupKey) {
        const normalizedGroupKey = this.normalizeKey(groupKey);
        const group = this.getGroupByKey(normalizedGroupKey);

        if (!group) return [];

        return this.getSelectedOptions(group).filter(option => this.optionBelongsToGroupKey(option, group, normalizedGroupKey));
    }

    isOptionAvailable(option) {
        const visibleIfGroup = option?.dataset.availableIfGroup;
        const visibleIfColourGroup = option?.dataset.availableIfColourGroup;

        if (visibleIfGroup || visibleIfColourGroup) {
            const selectedOptions = visibleIfGroup ? this.getSelectedOptionsForGroupKey(visibleIfGroup) : [];

            if (visibleIfColourGroup) {
                const normalizedColourGroup = this.normalizeKey(visibleIfColourGroup);

                if (!selectedOptions.some(selectedOption => this.normalizeKey(selectedOption.dataset.colourGroup) === normalizedColourGroup)) {
                    return false;
                }
            } else if (!selectedOptions.length) {
                return false;
            }
        }

        const unavailableIfGroup = option?.dataset.unavailableIfGroup;
        const unavailableIfValues = this.parseKeyList(option?.dataset.unavailableIfValues);

        if (unavailableIfGroup && unavailableIfValues.length) {
            const selectedValues = this.getSelectedValues(unavailableIfGroup);

            if (selectedValues.some(value => unavailableIfValues.includes(value))) {
                return false;
            }
        }

        const conditions = this.parseAvailableIf(option.dataset.availableIf);
        if (!conditions) return true;

        if (Array.isArray(conditions)) {
            return conditions.every(condition => this.matchesCondition(condition));
        }

        if (typeof conditions === 'object') {
            if (conditions.group || conditions.key || conditions.value || conditions.values) {
                return this.matchesCondition(conditions);
            }

            return Object.entries(conditions).every(([groupKey, allowedValues]) => {
                return this.matchesGroupValues(groupKey, allowedValues);
            });
        }

        return true;
    }

    matchesCondition(condition) {
        if (!condition || typeof condition !== 'object') return true;

        const groupKey = condition.group || condition.key || condition.groupKey;
        const allowedValues = condition.values || condition.value || condition.option || condition.options;

        if (!groupKey || typeof allowedValues === 'undefined') return true;

        return this.matchesGroupValues(groupKey, allowedValues);
    }

    matchesGroupValues(groupKey, allowedValues) {
        const selectedValues = this.getSelectedValues(groupKey);
        const allowed = Array.isArray(allowedValues) ? allowedValues : [allowedValues];

        return allowed.some(value => {
            const normalizedValue = this.normalizeKey(value);

            return selectedValues.some(selectedValue => selectedValue === normalizedValue);
        });
    }

    getSelectedValues(groupKey) {
        const selectedOptions = this.getSelectedOptionsForGroupKey(groupKey);

        return selectedOptions.flatMap(option => [
            this.normalizeKey(option.dataset.optionKey),
            this.normalizeKey(option.dataset.optionLabel),
        ]);
    }

    optionBelongsToGroupKey(option, group, normalizedGroupKey) {
        const normalizedGroupDatasetKey = this.normalizeKey(group.dataset.groupKey);
        const normalizedScopeKey = this.normalizeKey(option.dataset.optionScopeKey);

        if (normalizedScopeKey) return normalizedScopeKey === normalizedGroupKey;
        return normalizedGroupDatasetKey === normalizedGroupKey;
    }

    isOptionVisible(option) {
        return Boolean(
            option &&
            !option.hidden &&
            !option.classList.contains('is-hidden') &&
            !option.classList.contains('is-colour-group-hidden') &&
            !option.closest('[data-dependent-options][hidden]')
        );
    }

    isDependentPanelVisible(panel) {
        return Boolean(
            panel &&
            !panel.hidden &&
            !panel.classList.contains('is-hidden') &&
            panel.offsetParent !== null
        );
    }

    parseKeyList(rawValue) {
        return String(rawValue || '')
            .split('|')
            .map(value => this.normalizeKey(value))
            .filter(Boolean);
    }

    parseAvailableIf(rawValue) {
        if (!rawValue || rawValue === 'null' || rawValue === '""') return null;

        try {
            let parsed = JSON.parse(rawValue);
            if (typeof parsed === 'string') {
                const trimmed = parsed.trim();
                if (!trimmed || trimmed === 'null') return null;
                parsed = JSON.parse(trimmed);
            }

            return parsed;
        } catch (error) {
            return null;
        }
    }

    getOptionPriceCents(option) {
        const originalPriceCents = this.getOriginalOptionPriceCents(option);
        if (!this.isDiscountEligibleOption(option) || !this.discountPercent || !originalPriceCents) {
            return originalPriceCents;
        }

        const discountAmountCents = Math.round(Math.abs(originalPriceCents) * this.discountPercent / 100);
        return originalPriceCents > 0
            ? originalPriceCents - discountAmountCents
            : originalPriceCents + discountAmountCents;
    }

    getOriginalOptionPriceCents(option) {
        return this.toNumber(option?.dataset.priceCents, 0);
    }

    isDiscountEligibleOption(option) {
        const scope = option?.dataset.optionScopeKey || option?.dataset.optionScope || '';
        return scope === 'benchtop' || scope === 'basin' || scope === 'basin_colour';
    }

    getValidDiscountPercent(value) {
        const discount = Number(value);
        if (!Number.isFinite(discount) || discount < 0 || discount > 100) return 0;
        return Math.round(discount);
    }

    renderDiscountedOptionPrices(root = this) {
        if (!this.discountPercent) return;

        root.querySelectorAll('[data-vanity-option]').forEach(option => {
            if (!this.isDiscountEligibleOption(option)) return;

            const originalPriceCents = this.getOriginalOptionPriceCents(option);
            if (!originalPriceCents) return;

            const currentPriceCents = this.getOptionPriceCents(option);
            const priceEl = option.querySelector('.vanity-builder-option__price');
            if (!priceEl) return;

            priceEl.classList.add('is-discounted');
            priceEl.replaceChildren(this.createOptionPriceElement(
                'span',
                'vanity-builder-option__price-current',
                currentPriceCents,
            ));

            if (originalPriceCents > 0 && currentPriceCents < originalPriceCents) {
                priceEl.append(this.createOptionPriceElement(
                    's',
                    'vanity-builder-option__price-compare',
                    originalPriceCents,
                ));
            }
        });
    }

    createOptionPriceElement(tagName, className, cents) {
        const priceEl = document.createElement(tagName);
        priceEl.className = className;
        priceEl.textContent = this.formatOptionPrice(cents);
        return priceEl;
    }

    formatOptionPrice(cents) {
        const centsNumber = this.toNumber(cents, 0);
        if (centsNumber < 0) return this.formatMoney(centsNumber);
        return `+${this.formatMoney(centsNumber)}`;
    }

    scrollToFirstIncompleteGroup() {
        const group = this.groups.find(groupEl => this.isGroupAvailable(groupEl) && !this.isGroupComplete(groupEl));
        group?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    normalizeKey(value) {
        return String(value || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    toNumber(value, fallback) {
        const number = Number(value);
        return Number.isFinite(number) ? number : fallback;
    }

    formatMoney(cents) {
        const centsNumber = Number(cents || 0);
        const isNegative = centsNumber < 0;
        const absoluteCents = Math.abs(centsNumber);
        let formattedMoney = '';

        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
            formattedMoney = window.Shopify.formatMoney(absoluteCents);
        } else {
            formattedMoney = `$${(absoluteCents / 100).toLocaleString('en-AU', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })}`;
        }

        return isNegative ? `-${formattedMoney}` : formattedMoney;
    }
}

if (!customElements.get('vanity-builder')) {
    customElements.define('vanity-builder', VanityBuilder);
}

if (!window.__vanityBuilderScrollBound) {
    window.__vanityBuilderScrollBound = true;
    document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-vanity-builder-scroll]');
        if (!button || button.disabled) return;

        const targetId = button.getAttribute('aria-controls');
        const target = targetId ? document.getElementById(targetId) : document.querySelector('vanity-builder');
        if (!target) return;

        event.preventDefault();

        if (
            button.hasAttribute('data-vanity-builder-ready') &&
            typeof target.areRequiredGroupsComplete === 'function' &&
            target.areRequiredGroupsComplete()
        ) {
            const addButton = target.querySelector('[data-vanity-builder-add]');
            if (addButton && !addButton.disabled) {
                addButton.click();
                return;
            }
        }

        if (
            button.hasAttribute('data-vanity-summary-trigger') &&
            typeof target.openSummarySheet === 'function' &&
            typeof target.shouldStickyOpenSummary === 'function' &&
            target.shouldStickyOpenSummary()
        ) {
            target.openSummarySheet();
            return;
        }

        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
}
